// THE MODELS A KEY ADVERTISES: the allowed-model ceiling, one captured model row, the
// plausibility bounds a provider's self-reported numbers are filtered through, and the
// capture helper that folds a discovery into a stored list.
// Split out of `../byok.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { ReasoningCapabilitySchema } from '../../value-objects/reasoning-capability';
import { BYOKTierSchema } from './vocabulary';

/**
 * The wildcard entry in {@link AllowedModelsSchema}: "any model this endpoint
 * serves" (arc 101 slice 03). NOT "any string" — for a known provider the
 * recipient picks from the domain catalog; for a `custom` OpenAI-compatible
 * endpoint whose catalog we cannot enumerate, they type a model id and we accept
 * it, recording that it was unvalidated. We never probe the donor's key to
 * populate a dropdown — that spends their money.
 */
export const MODEL_WILDCARD = '*';

/**
 * The donor's model CEILING on a donated key (arc 101 slice 03) — what the gift
 * authorizes, which the recipient may narrow but never widen. `[MODEL_WILDCARD]`
 * = any model the endpoint serves; a concrete list = exactly that set. Absent
 * (null) is the LEGACY shape: exactly the key's own `model`, i.e. every
 * pre-arc key behaves byte-identically.
 *
 * Non-empty by construction: an empty list would authorize nothing while reading
 * like "no restriction", so it is rejected here rather than resolved later.
 * Entries are opaque provider model ids (bounded at 200 chars — marketplace hosts
 * like `together`/`fireworks` namespace theirs), never validated against a
 * catalog at this layer.
 */
export const AllowedModelsSchema = z.array(z.string().min(1).max(200)).min(1);
export type AllowedModels = z.infer<typeof AllowedModelsSchema>;

/**
 * Where a stored model limit came from (#4240) — `byok_key_models.context_length_source`
 * and `.max_output_tokens_source`, one per number, each CHECK-constrained to exactly
 * these two (migration `0334`). A declared table is
 * NOT a source here: a registry/prefix answer is `modelLimits`' (`@developerz/agent`)
 * fallback, never a stored fact.
 */
export const ModelLimitsSource = {
  ModelsApi: 'models_api',
  OverflowError: 'overflow_error',
} as const;
export const ModelLimitsSourceSchema = z.enum(ModelLimitsSource);
export type ModelLimitsSource = z.infer<typeof ModelLimitsSourceSchema>;

/**
 * One row of a key's tagged model catalogue (`byok_key_models`, arc 101 slice 02)
 * — "this credential can address THIS model, and it serves THIS lane tier".
 *
 * Distinct from {@link BYOKKeySchema.shape.tier}, which ranks the KEY: this ranks
 * a model WITHIN a key, which is what makes "several models at `smart`"
 * expressible at all ({@link BYOKKeySchema.shape.model} holds exactly one).
 *
 * Distinct again from {@link AllowedModelsSchema}, which is the DONOR's ceiling on
 * a donated key — an authorization bound the recipient may narrow but never widen.
 * This is the account's own catalogue of what it tagged. Two questions, two
 * fields; overloading either one collides with the donation fence.
 *
 * `tier` null/absent = UNTAGGED: the model is addressable but steers no tier, so
 * it ranks nothing — the state every migration-0165 backfilled row is in.
 * `active` false parks a tag without deleting it (the same on/off semantics as
 * {@link BYOKKeySchema.shape.active}); a parked model is invisible to resolution,
 * neither matched by a pin nor by a tier need.
 *
 * That last sentence is a UNIVERSAL, so it names its two enforcement points rather
 * than resting on one: `modelActive` (`value-objects/llm-credential/predicates.ts`)
 * for callers that resolve through `resolveLlmFromPool`, and `modelParkFence`
 * (`@developerz/db` `queries/byok/predicates.ts`) for the callers that pick a key by
 * QUERY. It had only the first from migration 0165 until #3491, and the SQL pickers
 * are what `apps/agents-manager` resolves its key through — so the sentence was true
 * of the resolver and false of the path production actually takes.
 *
 * SNAKE_CASE ON PURPOSE, unlike the camelCase entities around it: this schema IS
 * the capture wire, taken verbatim by four doors (`POST /v1/billing/keys`,
 * `POST /v1/me/keys`, MCP `billing key_add`, MCP `account key_add`), and its
 * siblings in those bodies are `base_url` and `api_key`. Same reason
 * `SealedLlmCredentialSchema` (`../value-objects/llm-credential.ts`) spells
 * `key_id`/`base_url`/`sealed_key`.
 */
export const BYOKKeyModelSchema = z.object({
  /** Provider model id, as the provider reports it — e.g. `claude-opus-4-8`. */
  model: z.string().min(1).max(200),
  /** Which lane tier this model serves; null/absent = untagged (steers nothing). */
  tier: BYOKTierSchema.nullish(),
  /** Absent = active (the resolver default), mirroring the key's own `active`. */
  active: z.boolean().optional(),
  /**
   * The PROVIDER-REPORTED context window for this model, in tokens (arc 101
   * slice 02) — OpenRouter's `context_length`, Groq's `context_window`, Google's
   * `inputTokenLimit`, all off the same `/models` response the picker enumerated.
   * Null/absent = the provider did not report one, which is most of them.
   *
   * It exists because GUESSING this number has already cost us a session:
   * `contextLengthFor` (`packages/agent/src/compaction.ts`) hardcoded a prefix
   * table that answered 1_000_000 for models whose real window is 200_000, so
   * compaction waited for 700_000 live tokens on a 200_000 window — it never
   * fired and the context overflowed instead. Detected beats declared: a value
   * here outranks `MODEL_REGISTRY`, which outranks the prefix table.
   *
   * PLAUSIBILITY IS NOT OPTIONAL on the way in — see
   * {@link plausibleContextLength}. A wrong window is worse than no window
   * because it fails silently and expensively, in tokens the customer paid for.
   */
  context_length: z.number().int().positive().nullish(),
  /**
   * What the PROVIDER charges for this model, USD per 1M INPUT tokens (arc 101
   * slice 02) — OpenRouter's `pricing.prompt`, off the same `/models` response
   * that carried {@link BYOKKeyModelSchema.shape.context_length}. Null/absent =
   * the provider published no rate card on its list, which is every provider but
   * OpenRouter today.
   *
   * PER 1M TOKENS, not per token, and the conversion happens once — at the
   * parser, where the provider's own unit is known. Every other rate in this
   * repo is per-Mtok (`MODEL_REGISTRY.inputPerMtok`,
   * `OpenRouterReference.inputPerMtok`, `RunUsagePriceBasis.inputPerMtok`), so
   * storing OpenRouter's per-token decimal verbatim would put a second unit in
   * the same vocabulary and the arithmetic would be wrong by 1e6 the first time
   * somebody read the wrong one.
   *
   * It exists because the two DECLARED rate tables cover almost nothing.
   * Measured on prod 2026-08-27: of 10,195 sessions with real tokens, 699 carry
   * a `reference_usd` (6.9%), totalling $21.00 against $2,032.15 billed, and
   * `MODEL_REGISTRY` carries no row at all for the BYOK ids prod actually runs.
   * A hand-curated table ages the moment it is written; this one is read off the
   * endpoint the key dials.
   */
  input_per_mtok: z.number().nonnegative().nullish(),
  /**
   * What the PROVIDER charges for this model, USD per 1M OUTPUT tokens —
   * OpenRouter's `pricing.completion`. Same unit, same source, same nullability
   * as {@link BYOKKeyModelSchema.shape.input_per_mtok}.
   *
   * The two are USED as a pair and are meaningless apart: a rate for half a run
   * prices nothing. `detectedModelRates` (`../value-objects/llm-credential`)
   * therefore answers with BOTH or with nothing, rather than letting a caller
   * assemble a half-detected price.
   */
  output_per_mtok: z.number().nonnegative().nullish(),
  /**
   * The provider-reported OUTPUT ceiling for this model, in tokens (#4240) —
   * OpenRouter's `top_provider.max_completion_tokens`, Groq's
   * `max_completion_tokens`, Google's `outputTokenLimit`, Anthropic's `max_tokens`.
   * READ-SIDE ONLY: detected server-side (`detectKeyModelLimits`,
   * `@developerz/billing`) and never taken from a capture body —
   * {@link CapturedKeyModelsSchema} omits it. Null/absent = none detected.
   */
  max_output_tokens: z.number().int().positive().nullish(),
  /**
   * Where {@link BYOKKeyModelSchema.shape.context_length} was LEARNED (#4240):
   * `models_api` (the provider's `/models` answer) or `overflow_error` (the
   * provider's own overflow refusal, which only ever LOWERS a window). Null/absent
   * = never detected — a capture-supplied window carries no source, and
   * `detectedModelLimits` does not answer for it. READ-SIDE ONLY, like
   * `max_output_tokens`. One source PER NUMBER (`byok_key_models`, migration 0334):
   * a learned window pins only the window.
   */
  context_length_source: ModelLimitsSourceSchema.nullish(),
  /**
   * Where {@link BYOKKeyModelSchema.shape.max_output_tokens} was LEARNED: `models_api`
   * or `overflow_error` (the provider's own `[1, N]` refusal, which only ever LOWERS
   * a ceiling). Null/absent = never detected. READ-SIDE ONLY.
   */
  max_output_tokens_source: ModelLimitsSourceSchema.nullish(),
  /**
   * What the provider says, or a one-time probe proved, about REASONING on this
   * model: supported or not, which control it takes (effort / budget / toggle /
   * always), the levels it names, whether the text comes back — see
   * {@link ReasoningCapabilitySchema}. Detected server-side beside the price and the
   * window (`detectKeyModelLimits`, `@developerz/billing`); READ-SIDE ONLY, like
   * `max_output_tokens` — {@link CapturedKeyModelsSchema} omits it. Null/absent =
   * never checked, and nothing is sent.
   */
  reasoning: ReasoningCapabilitySchema.nullish(),
  /**
   * Until when this ONE model is parked by its provider's own dated refusal
   * (2026-10-17) — a router 429 `…, earliest reset <ISO>` for one upstream sub, while
   * the key's other models still answer. Null/absent or past = not cooling. A cooling
   * model is invisible to resolution like a parked one ({@link modelLive}), but lapses
   * on its own: no write re-opens it. READ-SIDE ONLY — {@link CapturedKeyModelsSchema}
   * omits it, so a capture can never mint a park.
   */
  coolingUntil: z.coerce.date().nullish(),
  /**
   * The org deny list (#7810, `applyModelDenyList`, `../../value-objects/model-policy.ts`)
   * covers this model. Set only on the VIEW the pool read hands resolution, beside
   * `active: false`, so every predicate refuses the row as parked. It differs from a park
   * in one way: a denied OWN model steps the key to its next live model
   * (`resolveKeyModel`'s live-fallback rung) instead of dropping the key. The deny list
   * says "never this model", not "never this key". READ-SIDE ONLY: never stored, and
   * {@link CapturedKeyModelsSchema} omits it.
   */
  denied: z.boolean().optional(),
});
export type BYOKKeyModel = z.infer<typeof BYOKKeyModelSchema>;

/**
 * May resolution hand out this catalogue row at `now`: not parked (`active` false)
 * and not cooling past `now` (`coolingUntil`). The ONE liveness rule the resolver,
 * the park verdict and the consumers that write the park share.
 */
export function modelLive(m: BYOKKeyModel, now: Date): boolean {
  return m.active !== false && !(m.coolingUntil && m.coolingUntil > now);
}

/**
 * The door a dated per-model park came through — `input.door` on its
 * `billing.key.model_parked` row. The worker's four (`apps/worker`
 * `dispatch/key-model-park.ts`), the agents-manager session's one
 * (`wiring/key-model-park.ts`) and the verify probe's (`@developerz/billing`
 * `byok-verify-outcome.ts`); apps never import apps, so the vocabulary lives here.
 */
export type KeyModelParkDoor =
  | 'fleet.run_throttle'
  | 'fleet.key_failed'
  | 'fleet.credential_next'
  | 'in_process.key_wall'
  | 'session.credits_exhausted'
  | 'verify.quota';

/** What a dated refusal on ONE model of a key earns — see {@link modelParkOf}. */
export type ModelPark =
  | { readonly kind: 'park_model'; readonly model: string; readonly until: Date }
  | { readonly kind: 'cool_key' }
  | { readonly kind: 'none' };

/**
 * The verdict for a refusal on `model` that states `resetAt` (2026-10-17): park the
 * MODEL until the reset while some OTHER model on the key is still live — the key keeps
 * serving through it — and cool the whole KEY only when none is left. No stated reset
 * parks nothing: an undated park would be a guess at how long a quota lasts.
 *
 * Pure; the caller writes the verdict (`byok_key_models.cooling_until` or the key cool).
 */
export function modelParkOf(input: {
  readonly models: readonly BYOKKeyModel[];
  readonly model: string;
  readonly resetAt: Date | null;
  readonly now: Date;
}): ModelPark {
  if (input.resetAt === null) return { kind: 'none' };
  const otherLive = input.models.some((m) => m.model !== input.model && modelLive(m, input.now));
  return otherLive
    ? { kind: 'park_model', model: input.model, until: input.resetAt }
    : { kind: 'cool_key' };
}

/**
 * Floor for a believable OUTPUT ceiling, in tokens. Every chat model any lane here
 * runs emits at least 4K; a smaller number is a placeholder (Anthropic's docs
 * example answers `0`) or a non-chat row, and reads as ABSENT — the caller then
 * falls back to the declared tables, never to a tiny ceiling that truncates work.
 */
export const MIN_PLAUSIBLE_MAX_OUTPUT_TOKENS = 1_024;

/**
 * A reported output ceiling, or `undefined` when it is not one this platform will
 * act on. Bounded above by {@link MAX_PLAUSIBLE_CONTEXT_LENGTH}: an output ceiling
 * cannot exceed the window it is spent from, and OpenRouter reports
 * `max_completion_tokens` equal to a 943K window on some endpoints, so the window
 * bound is the honest one. Same two seams as {@link plausibleContextLength}: the
 * parser and the read.
 */
export function plausibleMaxOutputTokens(value: number | null | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value)) return undefined;
  return value >= MIN_PLAUSIBLE_MAX_OUTPUT_TOKENS && value <= MAX_PLAUSIBLE_CONTEXT_LENGTH
    ? value
    : undefined;
}

/**
 * Floor for an output ceiling LEARNED FROM A REFUSAL (`recordOutputCeilingRefusal`,
 * `@developerz/db`) — higher than {@link MIN_PLAUSIBLE_MAX_OUTPUT_TOKENS} on purpose.
 * That floor grades what a provider LISTS and is re-read on every refresh; a refusal
 * learned from a box is an only-lowers write no refresh undoes, so a buggy or hostile
 * box naming 1,024 would cap the key until an operator rewrote the row. 4K is the
 * minimum believable chat output the read floor's own docblock states ("every chat
 * model any lane here runs emits at least 4K").
 */
export const MIN_REFUSAL_LEARNED_MAX_OUTPUT_TOKENS = 4_096;

/**
 * A refusal-stated output ceiling this platform will STORE, or `undefined`: the read's
 * {@link plausibleMaxOutputTokens} plus the refusal floor
 * {@link MIN_REFUSAL_LEARNED_MAX_OUTPUT_TOKENS}.
 */
export function plausibleRefusalLearnedOutputCeiling(
  value: number | null | undefined,
): number | undefined {
  const graded = plausibleMaxOutputTokens(value);
  return graded !== undefined && graded >= MIN_REFUSAL_LEARNED_MAX_OUTPUT_TOKENS
    ? graded
    : undefined;
}

/**
 * Floor for a believable context window, in tokens.
 *
 * Under this a number is not a window this platform can act on: every model any
 * lane here runs ships at least 8K, so a smaller value is a placeholder, a
 * different unit, or a row for something that is not a chat model at all
 * (embedding endpoints report 512/2048 and appear in the same `/models` list).
 * Treated as ABSENT rather than as a tiny window, which is the conservative
 * direction — the caller then falls back to `MODEL_REGISTRY` and the prefix
 * table, i.e. exactly the behaviour it had before any of this existed.
 */
export const MIN_PLAUSIBLE_CONTEXT_LENGTH = 8_192;

/**
 * Ceiling for a believable context window, in tokens.
 *
 * 10 Mi — the largest window any model advertises today (Llama 4 Scout's 10M),
 * rounded up to the next power of two. Above it the number is not a token count:
 * a host reporting characters or bytes, a sentinel, or a field this parser
 * matched by name and misread. This bound is the one that MATTERS, because it is
 * the overstating direction: {@link MIN_PLAUSIBLE_CONTEXT_LENGTH} rejecting a
 * real value only compacts early, while accepting an inflated one is the
 * 700k-on-200k incident rebuilt from a provider's own JSON instead of ours.
 */
export const MAX_PLAUSIBLE_CONTEXT_LENGTH = 10_485_760;

/**
 * A reported context window, or `undefined` when it is not one this platform will
 * act on — the ONE definition of "plausible", so the write seam and the read seam
 * cannot disagree about which numbers count.
 *
 * Called at BOTH: capture clamps what a caller supplies, and
 * `contextLengthFor` re-checks what it read back. That is one function enforcing
 * one invariant at two points, not two rules — and the read side earns its call
 * because `byok_key_models` is reachable by raw INSERT, exactly as the `router`
 * pool rows in `byok_keys` demonstrably are (#1490). A guard only at the door is
 * a guard for the callers that use the door.
 */
export function plausibleContextLength(value: number | null | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value)) return undefined;
  return value >= MIN_PLAUSIBLE_CONTEXT_LENGTH && value <= MAX_PLAUSIBLE_CONTEXT_LENGTH
    ? value
    : undefined;
}

/**
 * Ceiling for a believable per-1M-token rate, in USD.
 *
 * The dearest model any provider publishes today is $600 per 1M output tokens
 * (`openai/o1-pro`), so this leaves better than an order of magnitude of headroom
 * for a frontier launch while still catching the failure that actually happens: a
 * UNIT slip. Provider rate cards are quoted per TOKEN (OpenRouter:
 * `"prompt": "0.000000834"`) and this platform stores per 1M, so the conversion is
 * a factor of 1e6 in one direction or the other — a per-Mtok figure multiplied a
 * second time lands at $834,000 and a $0.02 model lands at $20,000. Both are far
 * above this bound and neither can reach a customer's cost column.
 *
 * It does NOT catch a 1e3 slip (a per-1k-token card read as per-token), and
 * saying so is the point: the only body shape this platform parses a rate from is
 * one it has read live (OpenRouter's, verified 2026-08-29), so the bound is a
 * backstop for that endpoint changing units, not a general unit detector.
 */
export const MAX_PLAUSIBLE_TOKEN_RATE = 10_000;

/**
 * A reported per-1M-token rate, or `undefined` when it is not one this platform
 * will price with — the ONE definition of "plausible" for money, mirroring
 * {@link plausibleContextLength} for windows and enforced at the same three
 * points: the parser, the write seam, and the read.
 *
 * ZERO IS A REAL RATE AND IS KEPT. OpenRouter's `:free` variants publish
 * `"prompt": "0"`, and that is the truth about them — refusing it would push a
 * genuinely-free model onto `MODEL_REGISTRY`'s estimate for the paid variant,
 * which is the confident-wrong-number failure this whole column exists to remove.
 * A NEGATIVE rate is refused: it is not a discount, it is a parse error.
 *
 * Non-finite values (`NaN`, `Infinity`) are refused before the comparison, because
 * `NaN` fails every comparison silently and `Infinity` would otherwise depend on
 * the ceiling being written the right way round.
 */
export function plausibleTokenRate(value: number | null | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return value >= 0 && value <= MAX_PLAUSIBLE_TOKEN_RATE ? value : undefined;
}

/**
 * Ceiling on the catalogue ONE capture may write (arc 101 slice 02). Not a cost
 * cap — nothing here is billed — but a bound on rows a single request mints: the
 * body is a list a caller supplies and the write is one statement per entry, so
 * an unbounded array is an unbounded write. 50 is far above the shape the picker
 * produces (four tiers, a handful of models each) and far below anything that
 * reads as a bulk import.
 *
 * A body over the bound is REFUSED at the edge, never truncated: silently
 * dropping the 51st tag would leave a user looking at a select they filled in
 * and a key that never learned it.
 */
export const MAX_KEY_MODELS = 50;

/**
 * The tagged catalogue as it arrives at a CAPTURE door — `POST /v1/billing/keys`,
 * `POST /v1/me/keys` and the MCP `account key_add` verb, which is why it lives
 * here rather than beside any one of them.
 *
 * Model ids are UNIQUE within the list. `byok_key_models` carries a unique index
 * on `(key_id, model)`, so a repeated id is a constraint violation the caller can
 * do nothing with — refusing it at the edge names the field instead of surfacing
 * a 500 from a statement that already ran. Uniqueness is over the id ALONE: two
 * tiers for one model is precisely the contradiction this refuses, since
 * resolution would then answer by array order.
 */
export const CapturedKeyModelsSchema = z
  // DETECTED limits are never capture-supplied (#4240): a tenant posting
  // `context_length_source: 'models_api'` would otherwise mint a window the resolver
  // trusts as the provider's; a posted `coolingUntil` would mint a park. Zod strips the omitted keys, so an old client
  // sending them still captures.
  .array(
    BYOKKeyModelSchema.omit({
      max_output_tokens: true,
      context_length_source: true,
      max_output_tokens_source: true,
      reasoning: true,
      coolingUntil: true,
      denied: true,
    }),
  )
  .min(1)
  .max(MAX_KEY_MODELS)
  .refine((ms) => new Set(ms.map((m) => m.model)).size === ms.length, {
    message: 'each model may appear at most once',
  });
export type CapturedKeyModels = z.infer<typeof CapturedKeyModelsSchema>;

/**
 * The catalogue a capture actually writes: the caller's tags, plus an UNTAGGED
 * row for the key's own `model` when the tags do not already name it.
 *
 * The invariant is *every key's catalogue contains a row for its own `model`* —
 * which is exactly what migration 0165 backfilled for every pre-slice key, so a
 * key captured today and a key backfilled yesterday have the same shape and
 * `resolveLlmFromPool` has one case to reason about, not two. An absent list
 * therefore yields precisely the backfilled row, and `resolveKeyModel`'s
 * `ResolvedModelSource.Key` fallback stays reachable rather than becoming a
 * branch only legacy rows take.
 *
 * The caller's tag WINS when it names `model`: this never re-adds an id the
 * caller already tagged, so the `(key_id, model)` unique index cannot be
 * violated by the composition itself.
 */
export function captureKeyModels(
  model: string,
  tagged?: readonly BYOKKeyModel[] | null,
): readonly BYOKKeyModel[] {
  const tags = tagged ?? [];
  if (tags.some((m) => m.model === model)) return tags;
  return [...tags, { model, tier: null }];
}
